import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import type { CalendarDueItem, CalendarOccurrence, HomeSummary } from "@probetalab/contracts";
import { get, run } from "../lib/api.js";
import { byDay, calendarScope, daysOf, dueChip, dueTarget, periodLabel, rangeFor, shiftAnchor, visibleDue } from "../lib/calendar.js";
import { BranchLegend, MonthGrid } from "./Calendar.js";
import { type BranchToneOf, branchToneLookup, toneStyle } from "../lib/branchColors.js";
import { messageTime, useChat } from "../lib/chat.js";
import { fmtDueDay, plural } from "../lib/format.js";
import { greetingFor } from "../lib/quoteMessage.js";
import { useBranch, useSession } from "../lib/session.js";
import { useToast } from "../lib/toast.js";
import { errorMessage, useQuery } from "../lib/useQuery.js";
import { Button, Chip, Empty, IconButton, Loading } from "../components/ui.js";
import { Icon, type IconName } from "../components/icons.js";

/**
 * Inicio (Very, 2026-11-03 09:11: "que en el ícono nos lleve a una pantalla principal … el chat
 * entre sucursales, y … en grande los pendientes del día o tipo recordatorios"; Fer 10:01: "en
 * lugar de los pendientes, que se vea el calendario, y en otro apartado las notificaciones del
 * día"). The landing after sign-in and the sidebar mark's target (ADR 0028):
 *
 * 1. One compact strip (Fer 11:03: "un poco más compacto, para que todo el calendario se vea"): the
 *    greeting with the branch and date on one line, and today's six numbers beside it, each a door
 *    to its list. The whole month grid fits a 1080p screen without scrolling.
 * 3. The full month (Fer 10:02: "el calendario completo del mes y no por semana"), the same grid as
 *    Calendario → Mes (deliveries per day, the notes' titles, «+N más»), about two thirds of the
 *    width; tapping a day lists it underneath. It fills the space even when today is empty.
 * 4. «Avisos de hoy» beside it: overdue studies in red, today's deliveries, today's notes and sample
 *    appointments (tick to mark done). One line when there is nothing.
 * 5. The last messages of the Chat entre sucursales with «Abrir chat» (the same floating panel).
 */

interface Tile {
  key: keyof HomeSummary;
  label: string;
  icon: IconName;
  to: string;
  tone?: "amber" | "green" | "blue" | "red";
}

export function homeTiles(canCapture: boolean): Tile[] {
  return [
    { key: "orders", label: "Solicitudes de hoy", icon: "list", to: "/" },
    { key: "to_capture", label: "Por capturar", icon: "flask", to: canCapture ? "/captura" : "/", tone: "amber" },
    { key: "to_validate", label: "Por validar", icon: "badge", to: canCapture ? "/validacion" : "/", tone: "amber" },
    { key: "validated", label: "Listos para entregar", icon: "check", to: "/entrega", tone: "green" },
    { key: "delivered", label: "Entregados", icon: "send", to: "/entrega", tone: "blue" },
    { key: "pending_samples", label: "Muestras pendientes", icon: "dropper", to: "/muestras", tone: "red" },
  ];
}

/** «Avisos de hoy», in order: overdue, today's undelivered, today's notes (open ones first). */
export function todayAgenda(view: { today: string; due: CalendarDueItem[]; overdue: CalendarDueItem[]; entries: CalendarOccurrence[] }) {
  // Same-day studies are many and already in the tiles; the avisos list what has a turnaround.
  const due = view.due.filter((d) => d.due_date === view.today && d.status !== "delivered" && !d.same_day);
  const entries = view.entries.filter((e) => e.date === view.today).sort((a, b) => Number(a.done) - Number(b.done));
  return { overdue: view.overdue.filter((d) => !d.same_day), due, entries };
}

const labToday = () => new Intl.DateTimeFormat("en-CA", { timeZone: "America/Mexico_City" }).format(new Date());

export function HomePage() {
  const branch = useBranch();
  const { me, can } = useSession();
  const navigate = useNavigate();
  const toast = useToast();
  const chat = useChat();
  const canCapture = can("quimico");
  const today = labToday();
  const [month, setMonth] = useState(today);
  const [selected, setSelected] = useState(today);
  const [both, setBoth] = useState(false);
  const scope = calendarScope(both, branch, me?.branches ?? []);
  const toneOf = branchToneLookup(both, me?.branches ?? []);
  const range = rangeFor("month", month);
  const summary = useQuery(() => get("home", { query: { branch_id: branch.id } }), [branch.id]);
  const cal = useQuery(() => get("calendar", { query: { from: range.from, to: range.to, ...(scope.branchId ? { branch_id: scope.branchId } : {}) } }), [scope.branchId, range.from, range.to]);
  // «Avisos de hoy» always read today, whatever month is on screen.
  const todayView = useQuery(() => get("calendar", { query: { from: today, to: today, branch_id: branch.id } }), [branch.id, today]);
  const labTodayValue = todayView.data?.today ?? today;
  const avisos = useMemo(() => (todayView.data ? todayAgenda(todayView.data) : null), [todayView.data]);
  const dueByDay = useMemo(() => byDay(visibleDue(cal.data?.due ?? [], false), (d) => d.due_date), [cal.data]);
  const entriesByDay = useMemo(() => byDay(cal.data?.entries ?? [], (e) => e.date), [cal.data]);

  // The chat preview shows what the panel would; refreshed when Inicio opens and every minute after.
  useEffect(() => {
    if (!chat.enabled) return;
    void chat.refresh();
    const t = window.setInterval(() => void chat.refresh(), 60_000);
    return () => window.clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat.enabled]);

  const firstName = (me?.user.full_name ?? "").replace(/^(Q\.?\s*F\.?\s*B\.?|Dra?\.)\s*/i, "").split(/\s+/)[0] ?? "";
  const avisoCount = avisos ? avisos.overdue.length + avisos.due.length + avisos.entries.filter((e) => !e.done).length : 0;

  async function toggleDone(o: CalendarOccurrence) {
    try {
      await run("calendar.entry.done", { entry_id: o.entry.id, date: o.date, done: !o.done }, { branch_id: branch.id });
      await Promise.all([cal.reload(), todayView.reload()]);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }

  const open = (it: CalendarDueItem) => navigate(dueTarget(it, canCapture));
  const latest = chat.messages.slice(-5);
  const selDue = dueByDay.get(selected) ?? [];
  const selEntries = entriesByDay.get(selected) ?? [];
  const goMonth = (dir: -1 | 1) => {
    const next = shiftAnchor("month", month, dir);
    setMonth(next);
    setSelected(next.slice(0, 7) === today.slice(0, 7) ? today : next);
  };

  return (
    <div className="home">
      <div className="home-strip">
        <header className="home-hello">
          <h1 className="h1">
            {greetingFor(new Date())}
            {firstName ? `, ${firstName}` : ""}
          </h1>
          <p className="muted">
            <strong>{branch.name}</strong> · {fmtDueDay(today)}
          </p>
        </header>

        <section className="home-tiles" aria-label="Hoy en la sucursal" data-testid="home-tiles">
          {homeTiles(canCapture).map((t) => (
            <Link key={t.key} to={t.to} className={`home-tile ${t.tone ?? ""}`} data-testid={`tile-${t.key}`}>
              <span className="home-tile-icon">
                <Icon name={t.icon} />
              </span>
              <span className="home-tile-n">{summary.data ? (summary.data[t.key] as number) : "–"}</span>
              <span className="home-tile-label">{t.label}</span>
            </Link>
          ))}
        </section>
      </div>

      <div className="home-grid">
        <section className="card home-cal" aria-labelledby="home-cal-title" data-testid="home-calendar">
          <div className="card-head">
            <div className="home-cal-nav">
              <IconButton icon="chevronLeft" label="Mes anterior" onClick={() => goMonth(-1)} />
              <h2 className="h3" id="home-cal-title">
                {periodLabel("month", month)}
              </h2>
              <IconButton icon="chevronRight" label="Mes siguiente" onClick={() => goMonth(1)} />
              {month.slice(0, 7) !== today.slice(0, 7) && (
                <Button size="sm" variant="ghost" onClick={() => { setMonth(today); setSelected(today); }}>
                  Hoy
                </Button>
              )}
            </div>
            <div className="home-cal-tools">
              <span className="small muted" data-testid="home-cal-scope">{scope.label}</span>
              {(me?.branches.length ?? 0) > 1 && (
                <label className="cal-check">
                  <input type="checkbox" checked={both} onChange={(e) => setBoth(e.target.checked)} />
                  {scope.toggleLabel}
                </label>
              )}
              <Link to="/calendario" className="small">
                Abrir calendario
              </Link>
            </div>
          </div>
          <div className="card-body home-cal-body">
            {both && (me?.branches.length ?? 0) > 1 && <BranchLegend branches={me?.branches ?? []} toneOf={toneOf} />}
            <MonthGrid days={daysOf(range)} anchor={month} labToday={labTodayValue} dueByDay={dueByDay} entriesByDay={entriesByDay} onPickDay={setSelected} selected={selected} toneOf={toneOf} />
            <div className="home-day-list" aria-live="polite" data-testid="home-day-list">
              <div className="home-day-title">
                <strong>{selected === today ? "Hoy" : fmtDueDay(selected)}</strong>
                {selected === today ? <span className="muted"> · {fmtDueDay(today)}</span> : null}
              </div>
              {!cal.data ? (
                cal.error ? <p className="muted small">{cal.error}</p> : <Loading />
              ) : selDue.length + selEntries.length === 0 ? (
                <p className="muted small" style={{ margin: 0 }}>Sin entregas ni notas este día.</p>
              ) : (
                <div className="stack" style={{ gap: 8 }}>
                  {selEntries.map((o) => (
                    <NoteRow key={`${o.entry.id}-${o.date}`} o={o} onToggle={toggleDone} toneOf={toneOf} />
                  ))}
                  {selDue.map((it) => (
                    <AgendaDue key={it.order_item_id} it={it} onOpen={open} toneOf={toneOf} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>

        <div className="home-side">
          <section className="card home-avisos" aria-labelledby="home-avisos-title" data-testid="home-agenda">
            <div className="card-head">
              <h2 className="h3" id="home-avisos-title">
                Avisos de hoy {avisos && avisoCount > 0 ? <span className="home-count">{avisoCount}</span> : null}
              </h2>
            </div>
            <div className="card-body stack" style={{ gap: 8 }}>
              {!avisos ? (
                todayView.error ? <Empty icon="warning" title="No se pudieron cargar los avisos" hint={todayView.error} /> : <Loading />
              ) : avisos.overdue.length + avisos.due.length + avisos.entries.length === 0 ? (
                <p className="muted" style={{ margin: 0 }}>Nada pendiente para hoy.</p>
              ) : (
                <>
                  {avisos.overdue.length > 0 && <div className="kicker home-kicker late">Atrasados ({avisos.overdue.length})</div>}
                  {avisos.overdue.map((it) => (
                    <AgendaDue key={it.order_item_id} it={it} onOpen={open} />
                  ))}
                  {avisos.due.length > 0 && <div className="kicker home-kicker">Entregas de hoy ({avisos.due.length})</div>}
                  {avisos.due.map((it) => (
                    <AgendaDue key={it.order_item_id} it={it} onOpen={open} />
                  ))}
                  {avisos.entries.length > 0 && <div className="kicker home-kicker">Recordatorios y citas ({avisos.entries.length})</div>}
                  {avisos.entries.map((o) => (
                    <NoteRow key={`${o.entry.id}-${o.date}`} o={o} onToggle={toggleDone} />
                  ))}
                </>
              )}
            </div>
          </section>

          {chat.enabled && (
            <section className="card home-chat" aria-labelledby="home-chat-title" data-testid="home-chat">
              <div className="card-head">
                <h2 className="h3" id="home-chat-title">
                  Chat entre sucursales {chat.unread > 0 && <Chip tone="red">{plural(chat.unread, "sin leer", "sin leer")}</Chip>}
                </h2>
              </div>
              <div className="card-body stack" style={{ gap: 10 }}>
                {latest.length === 0 && <p className="muted" style={{ margin: 0 }}>Aún no hay mensajes.</p>}
                {latest.map((m) => (
                  <div key={m.id} className={`home-msg ${m.mine ? "mine" : ""}`}>
                    <span className="small muted">
                      <strong>{m.mine ? "Tú" : m.author_name}</strong> · {m.branch_name} · {messageTime(m.created_at)}
                    </span>
                    <span className={m.hidden ? "muted" : ""}>{m.hidden ? "Mensaje ocultado por su autor." : m.body}</span>
                  </div>
                ))}
                <Button variant="primary" icon="chat" onClick={() => chat.setOpen(true)}>
                  Abrir chat
                </Button>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

function NoteRow({ o, onToggle, toneOf }: { o: CalendarOccurrence; onToggle: (o: CalendarOccurrence) => void; toneOf?: BranchToneOf }) {
  const appointment = !!o.entry.patient_id;
  const tone = toneOf?.(o.entry.branch_id) ?? null;
  return (
    <div className={`home-item note ${o.done ? "done" : ""} ${appointment ? "appointment" : ""} ${tone ? "branch-tinted" : ""}`} style={toneStyle(tone)} data-testid="home-note" data-branch={tone ? (o.entry.branch_id ?? undefined) : undefined}>
      <label className="home-check" aria-label={o.done ? `Marcar «${o.entry.title}» como pendiente` : `Marcar «${o.entry.title}» como hecho`}>
        <input type="checkbox" checked={o.done} onChange={() => void onToggle(o)} />
      </label>
      <span className="home-item-main">
        <span className="home-item-title">
          {o.entry.entry_time ? <span className="mono">{o.entry.entry_time} </span> : null}
          {o.entry.title}
        </span>
        {(o.entry.patient_name || o.entry.note) && (
          <span className="small muted">
            {appointment ? "Cita · " : ""}
            {[o.entry.patient_name, o.entry.order_folio, o.entry.note].filter(Boolean).join(" · ")}
          </span>
        )}
      </span>
    </div>
  );
}

function AgendaDue({ it, onOpen, toneOf }: { it: CalendarDueItem; onOpen: (it: CalendarDueItem) => void; toneOf?: BranchToneOf }) {
  const chip = dueChip(it);
  const tone = it.overdue ? null : (toneOf?.(it.branch_id) ?? null);
  return (
    <button type="button" className={`home-item due ${it.overdue ? "overdue" : it.status} ${tone ? "branch-tinted" : ""}`} style={toneStyle(tone)} onClick={() => onOpen(it)} data-testid="home-due" data-branch={tone ? (it.branch_id ?? undefined) : undefined}>
      <span className="home-item-main">
        <span className="home-item-title">{it.patient_name}</span>
        <span className="small">
          <span className="mono">{it.study_clave}</span> {it.study_name} · <span className="mono">{it.folio}</span>
          {it.overdue ? ` · era para el ${fmtDueDay(it.due_date, true)}` : ""}
        </span>
      </span>
      <Chip tone={chip.tone}>{chip.label}</Chip>
      <Icon name="chevronRight" />
    </button>
  );
}
